// Rung 1 — meeting blips. This is the original single-page tool, moved as-is:
// same endpoints, same state machine, same copy, same limits. The only edits are
// structural — the page-level wrapper, the wordmark and the footer now live in
// Layout, and the results renderer moved to components/meeting/.

import { useRef, useState } from 'react';
import { useExtractMeeting, transcribeAudio } from '../lib/briefblip';
import { Results } from '../components/meeting/Results';

const MAX_CHARS = 60000;
const MAX_MB = 100;
const TEXT_EXTS  = ['txt', 'md'];
const AUDIO_EXTS = ['mp3', 'wav', 'ogg', 'oga', 'm4a', 'mp4', 'flac', 'webm', 'aac'];
const OK_EXTS    = [...AUDIO_EXTS, ...TEXT_EXTS];
const ACCEPT     = '.mp3,.wav,.ogg,.oga,.m4a,.mp4,.flac,.webm,.aac,.txt,.md,audio/*,video/mp4,text/plain,text/markdown';

type Mode = 'upload' | 'paste';
type UploadStage = 'idle' | 'uploading' | 'transcribing' | 'error';

export default function Meetings() {
  const [mode, setMode] = useState<Mode>('upload');
  const [transcript, setTranscript] = useState('');
  const [title, setTitle] = useState('');
  const extract = useExtractMeeting();
  const result = extract.data;

  const [stage, setStage] = useState<UploadStage>('idle');
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadErr, setUploadErr] = useState('');
  const [fileName, setFileName] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const currentExt = useRef('');

  const tooLong = transcript.length > MAX_CHARS;
  const canSubmit = transcript.trim().length > 0 && !tooLong && !extract.isPending;
  const busy = stage === 'uploading' || stage === 'transcribing' || extract.isPending;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    extract.mutate({ transcript: transcript.trim(), title: title.trim() || undefined });
  };

  const handleFile = async (file: File) => {
    setUploadErr('');
    extract.reset();
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    currentExt.current = ext;

    if (!OK_EXTS.includes(ext)) {
      setStage('error');
      setUploadErr(`Unsupported format: .${ext}. Use MP3, WAV, M4A, MP4, FLAC, OGG, WebM, AAC, TXT, or MD.`);
      return;
    }

    if (TEXT_EXTS.includes(ext)) {
      setFileName(file.name);
      setStage('uploading');
      const text = await file.text();
      const trimmed = text.trim();
      if (!trimmed) {
        setStage('error');
        setUploadErr('The file appears to be empty.');
        return;
      }
      if (trimmed.length > MAX_CHARS) {
        setStage('error');
        setUploadErr(`File too long (${trimmed.length.toLocaleString()} chars). Maximum ${MAX_CHARS.toLocaleString()}.`);
        return;
      }
      setTranscript(trimmed);
      setStage('idle');
      extract.mutate({ transcript: trimmed, title: title.trim() || undefined });
      return;
    }

    if (file.size > MAX_MB * 1024 * 1024) {
      setStage('error');
      setUploadErr(`File too large. Maximum ${MAX_MB} MB.`);
      return;
    }
    setFileName(file.name);
    setUploadPct(0);
    setStage('uploading');
    try {
      const res = await transcribeAudio(file, (pct) => {
        setUploadPct(pct);
        if (pct >= 100) setStage('transcribing');
      });
      setTranscript(res.transcript);
      setStage('idle');
      extract.mutate({ transcript: res.transcript.trim(), title: title.trim() || undefined });
    } catch (err) {
      setStage('error');
      setUploadErr(err instanceof Error ? err.message : 'Transcription failed.');
    }
  };

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) void handleFile(f);
    e.target.value = '';
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (busy) return;
    const f = e.dataTransfer.files?.[0];
    if (f) void handleFile(f);
  };

  return (
    <div className="mx-auto max-w-5xl px-5 py-10">
      <header className="mb-8 border-b border-edge pb-7 text-left sm:flex sm:items-end sm:justify-between sm:gap-8">
        <h1 className="wordmark mt-4 font-mono text-4xl font-bold tracking-tight sm:text-5xl">
          Meeting blips
        </h1>
        <p className="mt-3 max-w-xl text-base font-light text-[#5f6978]">
          Upload a recording or paste a transcript. Get the{' '}
          <b className="font-semibold text-[#17202c]">decisions</b>,{' '}
          <b className="font-semibold text-[#17202c]">action items</b>, and{' '}
          <b className="font-semibold text-[#17202c]">next steps</b> - in seconds.
        </p>
      </header>

      <div className="mb-6 flex justify-center">
        <div className="inline-flex rounded-lg border border-edge p-1">
          {(['upload', 'paste'] as Mode[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`rounded-md px-4 py-1.5 font-mono text-xs uppercase tracking-wider transition ${
                mode === m ? 'bg-cyan/15 text-cyan' : 'text-[#697386] hover:text-[#17202c]'
              }`}
            >
              {m === 'upload' ? 'Upload recording' : 'Paste transcript'}
            </button>
          ))}
        </div>
      </div>

      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Meeting title (optional)"
        maxLength={200}
        className="mb-4 w-full rounded-lg border border-edge bg-white/80 px-4 py-3 text-sm text-[#17202c] placeholder:text-[#8b94a3] focus:border-cyan focus:outline-none"
      />

      {mode === 'upload' ? (
        <div className="space-y-4">
          <div
            onClick={() => !busy && fileInput.current?.click()}
            onDragOver={(e) => { e.preventDefault(); if (!busy) setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-12 text-center transition ${
              dragOver ? 'border-cyan bg-cyan/5' : 'border-edge hover:border-cyan/50'
            } ${busy ? 'pointer-events-none opacity-60' : ''}`}
          >
            <input
              ref={fileInput}
              type="file"
              accept={ACCEPT}
              onChange={onPick}
              className="hidden"
            />
            {busy ? (
              <div className="w-full max-w-sm">
                <p className="mb-2 font-mono text-sm text-cyan">
                  {stage === 'uploading'
                    ? TEXT_EXTS.includes(currentExt.current) ? 'Reading...' : `Uploading... ${uploadPct}%`
                    : stage === 'transcribing'
                      ? 'Transcribing... this can take a moment'
                      : 'Generating brief...'}
                </p>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                  <div
                    className="h-full bg-gradient-to-r from-cyan to-magenta transition-all"
                    style={{ width: stage === 'uploading' ? `${uploadPct}%` : '100%' }}
                  />
                </div>
                {fileName && <p className="mt-2 truncate font-mono text-xs text-[#7a8493]">{fileName}</p>}
              </div>
            ) : (
              <>
                <p className="text-base text-[#17202c]">
                  Drop a file here, or <span className="text-cyan">browse</span>
                </p>
                <p className="mt-1 font-mono text-xs text-[#7a8493]">
                  MP3, WAV, M4A, MP4, FLAC, OGG, WebM, AAC - up to {MAX_MB} MB
                </p>
                <p className="mt-0.5 font-mono text-xs text-[#7a8493]">
                  TXT, MD (written transcript)
                </p>
              </>
            )}
          </div>

          {uploadErr && (
            <div className="rounded-lg border border-magenta/40 bg-magenta/10 px-4 py-3 text-sm text-[#ffd0ec]">
              {uploadErr}
            </div>
          )}

          {transcript && !busy && (
            <div>
              <textarea
                value={transcript}
                onChange={(e) => setTranscript(e.target.value)}
                rows={8}
                className="w-full resize-y rounded-lg border border-edge bg-white/80 px-4 py-3 font-mono text-sm leading-relaxed text-[#17202c] focus:border-cyan focus:outline-none"
              />
              <div className="mt-2 flex items-center justify-between">
                <span className="font-mono text-xs text-[#7a8493]">
                  Transcript ({transcript.length.toLocaleString()} chars) - edit if needed
                </span>
                <button
                  type="button"
                  onClick={() => onSubmit({ preventDefault() {} } as React.FormEvent)}
                  disabled={!canSubmit}
                  className="rounded-md border border-edge px-3 py-1.5 text-xs text-cyan transition hover:bg-cyan/10 disabled:opacity-40"
                >
                  Re-process
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <textarea
              value={transcript}
              onChange={(e) => setTranscript(e.target.value)}
              placeholder="Paste your transcript here..."
              rows={12}
              className="w-full resize-y rounded-lg border border-edge bg-white/80 px-4 py-3 font-mono text-sm leading-relaxed text-[#17202c] placeholder:text-[#8b94a3] focus:border-cyan focus:outline-none"
            />
            <div className="mt-1 flex justify-between font-mono text-xs text-[#7a8493]">
              <span>{transcript.length.toLocaleString()} / {MAX_CHARS.toLocaleString()} chars</span>
              {tooLong && <span className="text-magenta">Too long - split into shorter sections.</span>}
            </div>
          </div>

          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full rounded-lg bg-gradient-to-r from-cyan to-magenta px-6 py-3 font-semibold text-darker transition hover:shadow-glow disabled:cursor-not-allowed disabled:opacity-40"
          >
            {extract.isPending ? 'Processing...' : 'Process transcript'}
          </button>
        </form>
      )}

      {extract.isError && (
        <div className="mt-6 rounded-lg border border-magenta/40 bg-magenta/10 px-4 py-3 text-sm text-[#ffd0ec]">
          {(extract.error as Error).message}
        </div>
      )}

      {result && <Results result={result} />}
    </div>
  );
}
